import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SnowplowService } from '../services/snowplow';
import { makeEvent } from '../analytics/builder';
import { AssetsStore } from '../stores/assets.store';
import { AssetAnalysisItem } from '../services/api';
import { ParagraphLoader } from '../paragraph-loader/paragraph-loader';
import { TextCleanPipe } from '../pipes/text-clean-pipe';
import { ThemeService } from '../services/theme';
import { V2AnalysisHeader } from '../v2-analysis-header/v2-analysis-header';
import { formatAnalysisText, mapCompanyInsights } from '../services/format-analysis-text';

@Component({
  selector: 'app-analyse-body',
  imports: [ParagraphLoader, TextCleanPipe, V2AnalysisHeader],
  standalone: true,
  templateUrl: './analyse-body.html',
  styleUrl: './analyse-body.scss',
})
export class AnalyseBody implements OnDestroy, OnChanges {
  @Input() set analyseType(value: string) {
    this._analyseType.set(value);
  }
  private readonly _analyseType = signal<string>('');
  readonly analyseTypeSignal = this._analyseType;
  @Input() isin!: string | null;
  @Input() showBackButton = false;
  @Output() backClicked = new EventEmitter<void>();
  @ViewChild('bodyContainer') bodyContainer!: ElementRef<HTMLElement>;

  private readonly themeService = inject(ThemeService);
  readonly isFinnet = computed(() => this.themeService.isFinnet());

  private readonly snowplow = inject(SnowplowService);
  private elementViewedTimeout: ReturnType<typeof setTimeout> | undefined;

  // Handle depth scrolling based on analyse type changes (= "navigation")
  ngOnChanges(changes: SimpleChanges) {
    if (changes['analyseType']) {
      if (!changes['analyseType'].isFirstChange()) {
        this.clearElementViewedTimeout();
      }

      // Trigger "content read" tracking
      this.scheduleElementViewedAfterDelay();
    }
  }

  ngOnDestroy() {
    this.clearElementViewedTimeout();
  }

  private scheduleElementViewedAfterDelay() {
    const delayMs = 5000;
    this.clearElementViewedTimeout();

    this.elementViewedTimeout = setTimeout(() => {
      try {
        this.snowplow.track(
          makeEvent('element_read', {
            element_title: 'asset_analysis_read',
            feature_name: 'content_read',
          }),
        );
      } finally {
        // clear handle after firing
        this.clearElementViewedTimeout();
      }
    }, delayMs);
  }

  private clearElementViewedTimeout() {
    if (this.elementViewedTimeout) {
      clearTimeout(this.elementViewedTimeout);
      this.elementViewedTimeout = undefined;
    }
  }

  // Time ago (asset analysis)
  updatedAt = computed(() => {
    const v2Data = this.v2AssetData();
    return v2Data?.as_of ? new Date(v2Data.as_of) : null;
  });

  readonly companyUpdatedAt = computed(() => this.updatedAt() ?? new Date());

  // STORE DATA
  private readonly assetsStore = inject(AssetsStore);
  private readonly v2AssetData = this.assetsStore.v2Data;
  readonly loading = this.assetsStore.loading;

  // AnalyseBody is only ever reached for the company (action) tab — every other
  // analysis type has a dedicated V2 sub-page component (see core.html's @switch).
  private readonly currentV2GeneralAnalysis = computed<AssetAnalysisItem | null>(() => {
    const v2Data = this.v2AssetData();
    if (!v2Data) return null;
    const item = v2Data.analyses.find((a) => a.analysis_type === 'general');
    if (!item || item.output_type === 'cache_miss') return null;
    return item;
  });

  public formatText = formatAnalysisText;

  readonly sections = computed(() => mapCompanyInsights(this.currentV2GeneralAnalysis()?.insights));
}
